import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { message } from 'antd';
import { mapBackendError } from './errorMessages';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

const isAuthEndpoint = (url?: string) =>
  Boolean(url?.includes('/auth/login') || url?.includes('/auth/refresh'));

/**
 * Instanța axios comună.
 *
 * ATENȚIE — `Content-Type` NU e setat la nivel de instanță, intenționat.
 *
 * În axios, `transformRequest` decide cum e transformat corpul cererii **înainte**
 * de a ajunge la adapter. Dacă header-ul `Content-Type: application/json` e setat,
 * axios vede `FormData` și îl serializează în JSON (`formDataToJSON`), deci
 * fișierul binary dispare din cerere: serverul răspunde „Fișierul lipsește".
 *
 * Fără header pe instanță, axios pune singur `application/json` pentru payload-uri
 * obișnuite (obiecte) — deci apelurile JSON rămân corecte — iar pentru `FormData`
 * lasă browserul să seteze `multipart/form-data` cu boundary-ul propriu.
 */
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
});

// Request interceptor - attach JWT
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single-flight refresh: parallel 401s must share one rotation
// (a second concurrent refresh would present a already-consumed cookie).
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh`, {}, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        const payload = response.data.data ?? response.data;
        const { accessToken, refreshToken } = payload;
        useAuthStore.getState().updateTokens(accessToken, refreshToken);
        return accessToken as string;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Deduplicare toast-uri: același mesaj în fereastra DEDUP_WINDOW_MS
// este afișat o singură dată. Folosim conținutul mesajului ca și cheie
// de deduplicare și `key` AntD pentru a suprima dublurile identice.
const DEDUP_WINDOW_MS = 4000;
const lastShownAt = new Map<string, number>();

function showErrorToast(error: AxiosError) {
  const content = mapBackendError(error);
  const now = Date.now();
  const last = lastShownAt.get(content);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
    return; // duplicat suprimat
  }
  lastShownAt.set(content, now);

  // Curățări ocazionale ca să nu crească la infinit.
  if (lastShownAt.size > 200) {
    const cutoff = now - DEDUP_WINDOW_MS;
    for (const [k, t] of lastShownAt) {
      if (t < cutoff) lastShownAt.delete(k);
    }
  }

  message.error({ content, key: `api-error:${content}`, duration: 4 });
}

// Response interceptor - handle errors and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - refresh via httpOnly cookie, except for login/refresh failures.
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(originalRequest.url)) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Nu afișa toast pentru eșecuri din flow-ul de auth (login/refresh) —
    // paginile respective își afișează propriile mesaje pe formular.
    if (!isAuthEndpoint(originalRequest.url)) {
      showErrorToast(error);
    }

    return Promise.reject(error);
  }
);
